/**
 * Minimum patient age (in years) required to enroll, enforced by `buildEnrollmentSchema`.
 *
 * The `err.minAge` i18n message hard-codes "64" because the `t()` layer has no
 * parameter interpolation; keep that string in sync with this constant.
 */
export const MIN_ENROLLMENT_AGE = 64;

/**
 * True when a DOB of `dob` makes the person at least `minAge` years old as of
 * today (a birthday today counts as eligible, i.e. `>=`).
 *
 * `dob` must be the `YYYY-MM-DD` string a native `<input type="date">` yields.
 * A date-only integer comparison (no `Date.parse`/UTC) avoids the time-zone
 * off-by-one bugs of a millisecond comparison. Returns `false` for any value
 * that isn't a `YYYY-MM-DD` string, so callers should run their "valid date"
 * check first and leave only well-formed dates to this.
 */
export function isAtLeastAgeYears(dob: string, minAge: number): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!m) return false;
  const dobVal = Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
  const now = new Date();
  const cutoffVal =
    (now.getFullYear() - minAge) * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  return dobVal <= cutoffVal; // birthday-today counts as eligible (>=)
}

/**
 * Completed age in whole years as of today for a `YYYY-MM-DD` DOB string, or
 * `null` for empty/malformed/future input. Uses the same date-only (no UTC
 * parse) accessors as `isAtLeastAgeYears` so the two never disagree on a boundary.
 */
export function ageInYears(dob: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  if (!m) return null;
  const y = Number(m[1]),
    mo = Number(m[2]),
    d = Number(m[3]);
  const now = new Date();
  let age = now.getFullYear() - y;
  const hadBirthdayThisYear =
    now.getMonth() + 1 > mo || (now.getMonth() + 1 === mo && now.getDate() >= d);
  if (!hadBirthdayThisYear) age -= 1;
  return age < 0 ? null : age;
}
